import {useEffect, useState, useCallback, useMemo, useContext} from "react"
import { ItemWarehouse } from './api/itemwarehouse'
import { useParams, useNavigate } from "react-router-dom";
import {PASOverviewTable} from "./PASOverviewTable"
import "./PASOverview.scss"
import {useDivision} from "./shared"
import {AllDataContext} from "./LoggedinMainPage"
import TextAreaWithSuggestions from "./inputs/TextAreaWithSuggestions"

export default function PASOverview() {
  const allData = useContext(AllDataContext)
  const [negativeToOOS, setNegativeToOOS] = useState(false)
  const [showCounts, setShowCounts] = useState(false)
  const {warehouses} = allData
  const [overviewLinesString, setOverviewLinesString] = useState("")
  const division = useDivision()
  const itemPickerTrigger = useMemo(() => {
    const itemPickerData = allData.items.map(item => [
      item.code, item.code + " \u2014 " + item.description] as [string, string])
    return {"#": itemPickerData}
  }, [allData.items])


  const warehouseCode = useParams().warehouseCode ?? ""
  const overviewLinesStringParam = useParams().overviewLines ?? ""
  const navigate = useNavigate()

  useEffect(() => {
    setOverviewLinesString(overviewLinesStringParam)
  }, [overviewLinesStringParam])

  const overviewLines = overviewLinesString.trim().split("\n")
  const itemWarehouses = allData.itemWarehouses.filter(
    iw => iw.warehouseCode === warehouseCode)

  const doNavigate = useCallback(
    ({newWarehouseCode, newItemCodesString, replace}:
      {newWarehouseCode?: string, newItemCodesString?: string,
        replace?: boolean,}) => {
      navigate([
        "",
        "division",
        division,
        "PASOverview",
        "warehouse",
        newWarehouseCode === undefined ? warehouseCode : newWarehouseCode,
        "overviewlines",
        newItemCodesString === undefined ? overviewLinesString : newItemCodesString,
      ].map(encodeURIComponent).join("/"), {replace: replace ?? false})
    }, [navigate, division, warehouseCode, overviewLinesString])

  useEffect(function selectMainWarehouseIfNoneSelected() {
    if (warehouses.find(w => w.code === warehouseCode)) {
      return
    }
    const mainWarehouse = warehouses.find(w => w.main === 1) ?? warehouses[0]
    if (mainWarehouse) {
      doNavigate({newWarehouseCode: mainWarehouse.code, replace: true})
    }
  }, [doNavigate, division, warehouseCode, warehouses])

  const itemWarehouseByItemCode = new Map<string, ItemWarehouse>(
    itemWarehouses.map(itemWarehouse => [itemWarehouse.itemCode, itemWarehouse]))

  return <div className="PASOverview">
    <div className="warehouseSelector">
        <span>Select a warehouse: </span>
          <select data-cy="select-warehouse" value={warehouseCode} onChange={
            e => doNavigate({newWarehouseCode: e.target.value})}>
            {[...warehouses].sort((a, b) => a.code.localeCompare(b.code)).map(
              warehouse => <option key={warehouse.code} value={warehouse.code}>
                {warehouse.code} - {warehouse.description}
                </option>
            )}
          </select>
    </div>
    <div className="overviewLines">
      <details>
        <summary>Build your overview (click here for details)</summary>
        <p>Each line should be:</p>
        <ul>
          <li>A section header, followed by a colon (e.g. The Stereos:)</li>
          <li>
            A valid itemCode (e.g. KS0035WS).
            After this itemCode you can optionally add more info seperated by
            commas:
            <ul>
              <li>The first item is the itemCode</li>
              <li>
                The second item is the warning level
                (below which level the stock level will go orange)
              </li>
              <li>
                The third item is the info level
                (below which level the stock level will go light green)
              </li>
              <li>The fourth item is a comment for this line</li>
              <li>
                Note that you can leave values empty, e.g.  <code>
                  KS0035WS,,180,Problem at factory results in long lead time
                </code>
              </li>
            </ul>
          </li>
          <li>Be empty, for an empty line in the overview</li>
        </ul>
        <p>
          Once your're done building your overview and all lines finished
          loading, you can press the full-screen button and print the page
          to PDF (tip: scale the page to make smaller if it doesn't fit on one
          page), of simply share the url of this page with a colleage
          (or save it for later, it will always generate the overview as
           it is on the day you open the page)
        </p>
        <p>
          Obviously you can also copy the input you made and save it for later.
        </p>
        <p>
          <strong>Tip</strong>:
          Open <a href={
            URL.createObjectURL(
              new Blob([[
                "Title: or itemcode,warning level,info level,comment",
                "Section 1:",
                "ABC123,,200,For this item 200 is full stock",
                "DEF456,50",
                "",
                "Section 2:",
                "GHI789",
                "etc....",
              ].join("\n")],
              {type: "text/csv"}))}
          download="Projected Available Stock.csv">this file</a> in
          Excel or Google Sheets or whatever speadsheet you use.
          Create your list of items and headers in there,
          and then copy-pase everything except the first line to the text
          field here (this works because the text field will interpret
          tabs as commas).
        </p>
      </details>
      <TextAreaWithSuggestions
        triggers={itemPickerTrigger}
        setValue={setOverviewLinesString}
        onBlur={e => doNavigate({newItemCodesString: e.target.value})}
        style={{height:
          `${overviewLinesString.split("\n").length * 1.3 + 2.5}em`}}
        id="itemcodes"
        placeholder="Copy-paste the list of item codes you want the
        report for." value={overviewLinesString} />
    </div>
    <div>
      <input type="checkbox" id="showCounts" checked={showCounts}
        className="slider"
        onChange={e => setShowCounts(e.target.checked)} />
      <label htmlFor="showCounts">
        Show counts in the overview
      </label>
    </div>
    <div>
      <input type="checkbox" id="negativeToOOS" checked={negativeToOOS}
        className="slider"
        onChange={e => setNegativeToOOS(e.target.checked)} />
      <label htmlFor="negativeToOOS">
        Hide negative number errors (show out-of-stock instead)
      </label>
    </div>
    <hr />
    <PASOverviewTable
      {...{warehouseCode, itemWarehouseByItemCode, negativeToOOS, showCounts}}
      lines={overviewLines}
    />
  </div>
}
